import { FlatTreeControl } from "@angular/cdk/tree";
import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { MatTreeFlatDataSource, MatTreeFlattener, MatTreeNode } from "@angular/material/tree";
import { ClassificationService } from "../work-classification.service";
import { WorkClassification } from "src/app/shared/Models/hourentry";
import { AppState } from "src/app/state/app.reducer";
import { Store } from "@ngrx/store";
import { selectCurrentProject, selectCurrentProjectId } from "src/app/state/app.selectors";
import { WorkClassificationFormComponent } from "../work-classification-form/work-classification-form.component";
import { MatDialog } from "@angular/material/dialog";
import { BaseComponent } from "src/app/shared/components/base-component";
import { ConfirmationService } from "src/app/shared/services/utilities/confirmation.service";
import { CopyClassificationsToProjectDialogComponent } from "./copy-to-project-dialog/copy-to-project-dialog.component";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { firstValueFrom } from 'rxjs';
import { SnackBarService } from 'src/app/shared/services/snack-service.service';
import { TranslateService } from '@ngx-translate/core';
import { LoggerService } from 'src/app/shared/services/logger.service';


interface ClassificationNode {
  name: string;
  description: string;
  children: ClassificationNode[];
  parentClassificationId: number;
  id: number;
  level: number;
  expandable: boolean;
}

@Component({
    selector: 'app-classification-tree',
    templateUrl: './work-classification-tree.component.html',
    styleUrls: ['./work-classification-tree.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WorkClassificationTreeComponent extends BaseComponent implements OnInit {
  project = this.store.select(selectCurrentProject);
  expandedNodeIds: number[] = [];
  nodeStates: { [key: number]: string } = {};
  treeControl = new FlatTreeControl<ClassificationNode>(
    node => node.level,
    node => node.expandable,
  );
  classifications: WorkClassification[] = [];
  flatClassifications: WorkClassification[] = [];
  private _transformer = (node: ClassificationNode, level: number) => {
    return {
      expandable: !!node.children && node.children.length > 0,
      name: node.name,
      level: level,
      id: node.id,
      children: node.children,
      description: node.description,
      parentClassificationId: node.parentClassificationId,
    };
  };
  treeFlattener = new MatTreeFlattener(
    this._transformer,
    node => node.level,
    node => node.expandable,
    node => node.children,
  );
  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  projectId: number | undefined;
  newNodeId: number | null = null;
  isExporting: boolean = false;

  constructor(
    private classificationService: ClassificationService,
    private store: Store<AppState>,
    private dialog: MatDialog,
    private confirmService: ConfirmationService,
    private snackBarService: SnackBarService,
    private translate: TranslateService
  ) {
    super();
  }

  private logger = inject(LoggerService);

  ngOnInit(): void {
    this.store.select(selectCurrentProjectId).subscribe(projectId => {
      if (projectId) {
        this.projectId = projectId;
        this.loadClassifications(projectId);
      }
    });
  }

  setNodeHighlight(nodeId: number): void {
    this.nodeStates[nodeId] = 'highlighted';
    setTimeout(() => {
      this.nodeStates[nodeId] = 'normal';
    }, 1000);
  }

  loadClassifications(projectId: number) {
    this.classificationService.getClassifications(projectId).subscribe(classifications => {
      this.dataSource.data = classifications.map(c => this.convertToClassificationNode(c, 0));
      this.classifications = classifications;
      this.flatClassifications = classifications.flatMap(c => this.flattenClassifications(c));
      this.restoreExpandedNodes();
    });
  }
flattenClassifications(classification: WorkClassification): WorkClassification[] {
    return [classification, ...(classification.children || []).flatMap(c => this.flattenClassifications(c))];
  }
  convertToClassificationNode(classification: WorkClassification, level: number): ClassificationNode {
    return {
      name: classification.name,
      description: classification.description,
      id: classification.id,
      level: level,
      children: classification.children?.map(sub => this.convertToClassificationNode(sub, level + 1)) || [],
      parentClassificationId: classification.parentId || 0,
      expandable: !!classification.children && classification.children.length > 0,
    };
  }

  copyAllClassifications() {
    let dialogRef = this.dialog.open(CopyClassificationsToProjectDialogComponent, {
      width: '35rem',
      autoFocus: false,
      data: { fromProject: this.projectId },
    });

    dialogRef.afterClosed().subscribe((projectId: number) => {
      if (projectId) {
        this.classificationService.copyClassifications(this.projectId!, projectId).subscribe(() => {
          this.loadClassifications(this.projectId!);
        });
      }
    });
  }

  addSubClassification(node: ClassificationNode | null): void {
    const dialogRef = this.dialog.open(WorkClassificationFormComponent, {
      width: '35rem',
      autoFocus: false,
      data: { parentId: node ? node.id : null },
    });
    dialogRef.componentInstance.classificationCreated.subscribe((newClassification: WorkClassification) => {
      this.saveExpandedNodes();
      if (newClassification.parentId && !this.expandedNodeIds.includes(newClassification.parentId)) {
        this.expandedNodeIds.push(newClassification.parentId);
      }
      this.newNodeId = newClassification.id;
      this.setNodeHighlight(this.newNodeId);
      this.loadClassifications(newClassification.projectId);
      this.restoreExpandedNodes();
    });
  }

  saveExpandedNodes(): void {
    this.expandedNodeIds = this.treeControl.dataNodes
      .filter(node => this.treeControl.isExpanded(node))
      .map(node => node.id);
  }

  restoreExpandedNodes(): void {
    this.treeControl.dataNodes.forEach(node => {
      if (this.expandedNodeIds.includes(node.id)) {
        this.treeControl.expand(node);
      }
    });
  }

  editClassification(node: ClassificationNode): void {
    const classification = this.flatClassifications.find(c => c.id === node.id);
    const dialogRef = this.dialog.open(WorkClassificationFormComponent, {
      width: '35rem',
      autoFocus: false,
      disableClose: true,
      data: { classification: classification },
    });

    dialogRef.componentInstance.classificationCreated.subscribe((updatedClassification: WorkClassification) => {
      this.saveExpandedNodes();
      if (updatedClassification.parentId && !this.expandedNodeIds.includes(updatedClassification.parentId)) {
        this.expandedNodeIds.push(updatedClassification.parentId);
      }
      this.newNodeId = updatedClassification.id;
      this.setNodeHighlight(this.newNodeId);
      this.loadClassifications(updatedClassification.projectId);
    });
  }

  deleteClassification(node: ClassificationNode): void {
    this.confirmService.confirm('Are you sure you want to permanently delete this classification?', 'Confirm', () => {
      this.classificationService.deleteClassification(node.id).subscribe(
        () => {
          this.saveExpandedNodes();
          this.loadClassifications(this.projectId!);
        },
        error => {
          // Error handled silently, snackbar will be shown by service
        }
      );
    }, () => { }, 'Delete', 'warn', 'delete');
  }

  async exportTreeToPDF(): Promise<void> {
    this.isExporting = true;

    try {
      // Get current project
      const currentProject = await firstValueFrom(this.project);
      if (!currentProject) {
        throw new Error('No project selected');
      }

      // Create PDF
      const pdf = new jsPDF('portrait', 'px', 'a4', true);

      // Set PDF metadata
      pdf.setProperties({
        title: this.translate.instant('WORKCLASSIFICATION.EXPORTTITLE'),
        subject: 'LTQ-Rakennus Oy',
        author: 'LTQ-Rakennus Oy',
      });

      // Try to load logo (optional - continue without if fails)
      let startY = 40;
      try {
        const logoUrl = 'assets/images/ltqrakennus.png';
        const logoImage = await fetch(logoUrl)
          .then((r) => r.blob())
          .then((b) => URL.createObjectURL(b));
        pdf.addImage(logoImage, 'PNG', 10, 10, 80, 48, '', 'SLOW');
        startY = 80;
      } catch (logoError) {
        // Logo load failed - continue without it
      }

      // Add title
      pdf.setFontSize(22);
      const title = `${this.translate.instant('WORKCLASSIFICATION.EXPORTTITLE')} - ${currentProject.name}`;
      const pageWidth = pdf.internal.pageSize.width;
      const pageHeight = pdf.internal.pageSize.height;
      const txtWidth = (pdf.getStringUnitWidth(title) * pdf.getFontSize()) / pdf.internal.scaleFactor;
      const x = (pageWidth - txtWidth) / 2;
      pdf.text(title, x, 30);

      let currentY = startY;

      // Generate separate table for each root classification
      for (const rootClassification of this.classifications) {
        // Check if we need a new page
        if (currentY > pageHeight - 100) {
          pdf.addPage();
          currentY = 40;
        }

        // Add root classification title
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text(rootClassification.name, 15, currentY);
        currentY += 20;

        // Add root description if exists
        if (rootClassification.description) {
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'italic');
          const descLines = pdf.splitTextToSize(rootClassification.description, pageWidth - 30);
          pdf.text(descLines, 15, currentY);
          currentY += (descLines.length * 12) + 10;
        }

        // Build table data for children only
        const tableData = this.buildPDFTableDataForChildren(rootClassification.children || []);

        if (tableData.length > 0) {
          // Generate table for children
          autoTable(pdf, {
            startY: currentY,
            head: [['Alaluokitus', 'Kuvaus']],
            body: tableData,
            theme: 'grid',
            columnStyles: {
              0: { cellWidth: 'auto' },
              1: { cellWidth: 'auto' }
            },
            margin: { left: 15, right: 15 }
          });

          currentY = (pdf as any).lastAutoTable.finalY + 20;
        } else {
          currentY += 10;
        }
      }

      // Save PDF
      const fileName = `Tuntiluokittelut_${currentProject.name}.pdf`;
      pdf.save(fileName);

    } catch (error) {
      this.logger.errorWithContext('WorkClassificationTreeComponent', 'Failed to export PDF', error as Error);
      this.snackBarService.error(
        this.translate.instant('WORKCLASSIFICATION.EXPORTFAILED')
      );
    } finally {
      this.isExporting = false;
    }
  }

  private buildPDFTableData(classifications: WorkClassification[], level: number = 0): string[][] {
    const rows: string[][] = [];

    for (const classification of classifications) {
      // Create indented name using spaces and hyphen prefix
      const indent = '  '.repeat(level);
      const prefix = level > 0 ? '- ' : '';
      const indentedName = indent + prefix + classification.name;
      const description = classification.description || '';

      rows.push([indentedName, description]);

      // Recursively add children
      if (classification.children && classification.children.length > 0) {
        rows.push(...this.buildPDFTableData(classification.children, level + 1));
      }
    }

    return rows;
  }

  private buildPDFTableDataForChildren(classifications: WorkClassification[], level: number = 0): string[][] {
    const rows: string[][] = [];

    for (const classification of classifications) {
      // Create indented name using spaces only
      const indent = '  '.repeat(level);
      const indentedName = indent + classification.name;
      const description = classification.description || '';

      rows.push([indentedName, description]);

      // Recursively add children
      if (classification.children && classification.children.length > 0) {
        rows.push(...this.buildPDFTableDataForChildren(classification.children, level + 1));
      }
    }

    return rows;
  }

  hasChild = (_: number, node: ClassificationNode) => node.expandable;
}
